import React, { JSX } from "react";

import {
  debounce,
  ExpandableRow,
  IExpandableRowControl,
  ReorderableList,
} from "component-library";
import { dataTestIds } from "data-testids";
import { BottomIcon, TopIcon, ArrowIcon, EditIcon, DeleteIcon } from "assets";
import { IAccordionContent } from "./AccordionContent";

import "./Accordion.scss";

export enum AccordionAction {
  arrowBottom = 1,
  arrowTop,
  arrowDown,
  arrowUp,
  edit,
  delete,
}

type AccordionActionType = {
  handler: (index: number) => () => void;
  svgIcon: React.ReactNode;
  name: AccordionAction;
  disabled?: boolean;
  ariaLabel?: string;
};

export interface IAccordionProps<T> {
  accordionRowsData: IAccordionRowData<T>[];
  actions: AccordionAction[];
  actionHandler: (
    uniqueKey: string | number,
    actionType: AccordionAction
  ) => void;
  content?: IAccordionContent;
  sortFunction?: () => void;
  itemsReordered?: (newKeysOrder: (string | number)[]) => void;
  isInitiallyExpanded?: boolean;
  immovableTail?: JSX.Element;
}

export interface IAccordionRowData<T> {
  uniqueKey: string | number;
  header: string;
  renderChildren: (data: T) => JSX.Element;
  data: T;
}

interface IAccordionState<T> {
  accordionRowsData: IAccordionRowData<T>[];
  disableReordering: boolean;
}

interface IReorderableListItemData<T> extends IAccordionRowData<T> {
  controls: IExpandableRowControl[];
}

export class Accordion<T> extends React.Component<
  IAccordionProps<T>,
  IAccordionState<T>
> {
  private rowsRef = new Map<string | number, ExpandableRow>();

  constructor(props: IAccordionProps<T>) {
    super(props);

    this.state = {
      accordionRowsData: props.accordionRowsData,
      disableReordering: false,
    };
  }

  public render() {
    const { content, immovableTail } = this.props;

    const reorderableListItems = this.getReorderableListItems();

    return (
      <div className="c-accordion">
        <div className="c-accordion__header">
          {content && this.renderTopBar(content)}
        </div>
        <div className="c-accordion__container">
          <ReorderableList
            axis="y"
            lockAxis="y"
            distance={5}
            list={reorderableListItems}
            itemRenderer={this.itemRenderer}
            onReorderEnd={this.onReorderEnd}
            getUniqueKey={this.getUniqueKey}
            disableReordering={this.state.disableReordering}
            useAnimatedList={true}
            immovableTail={immovableTail as any}
          />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    window.addEventListener("resize", this.handleResize, false);
    this.handleResize();
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize, false);
  }

  // eslint-disable-next-line react/no-deprecated
  public componentWillReceiveProps(newProps: IAccordionProps<T>) {
    if (this.props.accordionRowsData !== newProps.accordionRowsData) {
      this.rowsRef = new Map();
      this.setState({ accordionRowsData: newProps.accordionRowsData });
    }
  }

  public expandItem = (uniqueKey: string): any => {
    const item = this.rowsRef.get(uniqueKey);

    if (item) {
      item.expand();

      return item;
    }
  };

  private renderTopBar = (content: IAccordionContent) => {
    const { sortFunction } = this.props;

    const sortButton = sortFunction && (
      <React.Fragment>
        <a
          className="c-accordion__control g-alternative-link"
          tabIndex={0}
          onClick={sortFunction}
          onKeyPress={this.handleKeyPress(sortFunction)}
        >
          {content.changeOrder}
        </a>
        <div className="c-accordion__control-divider">|</div>
      </React.Fragment>
    );

    return (
      <div className="c-accordion__control-group">
        {sortButton}
        <a
          className="c-accordion__control g-alternative-link"
          tabIndex={0}
          onClick={this.collapseAll}
          onKeyPress={this.handleKeyPress(this.collapseAll)}
        >
          {content.collapseAll}
        </a>
        <div className="c-accordion__control-divider">|</div>
        <a
          className="c-accordion__control g-alternative-link"
          tabIndex={0}
          onClick={this.expandAll}
          onKeyPress={this.handleKeyPress(this.expandAll)}
        >
          {content.expandAll}
        </a>
      </div>
    );
  };

  private handleKeyPress = (action: () => void) => {
    return (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key == "Enter" || event.key == " ") {
        event.preventDefault();
        action();
      }
    };
  };

  private handleResize = debounce(() => {
    const disableReordering = window.innerWidth < 1200;
    this.setState({ disableReordering });
  }, 300);

  private itemRenderer = ({
    controls,
    header,
    uniqueKey,
    renderChildren,
    data,
  }: IReorderableListItemData<T>) => (
    <ExpandableRow
      controls={controls}
      header={header}
      data-testid={
        dataTestIds.profile["Accordion.AccordionItem.expandable-row"]
      }
      ref={(r) => r && this.rowsRef.set(uniqueKey, r)}
      isInitiallyExpanded={this.props.isInitiallyExpanded}
      toggleAriaLabel={header}
    >
      {renderChildren(data)}
    </ExpandableRow>
  );

  private onReorderEnd = ({ oldIndex, newIndex, newList }) => {
    if (oldIndex !== newIndex) {
      this.setReorderedRowsData(newList);
    }
  };

  private getUniqueKey = (row: IReorderableListItemData<T>) => row.uniqueKey;

  private getReorderableListItems = (): IReorderableListItemData<T>[] => {
    const rowsData = [...this.state.accordionRowsData];

    return rowsData.map((data, index) => ({
      ...data,
      controls: this.getRowControls(index, rowsData.length - 1, data.uniqueKey),
    }));
  };

  private getRowControls = (
    index: number,
    maxIndex: number,
    uniqueKey: string | number
  ): IExpandableRowControl[] => {
    return this.getActions(uniqueKey)
      .filter(
        (action) =>
          this.props.actions.findIndex((item) => item === action.name) > -1
      )
      .map((action) => ({
        disabled:
          action.disabled ||
          (index === 0 &&
            (action.name === AccordionAction.arrowTop ||
              action.name === AccordionAction.arrowUp)) ||
          (index === maxIndex &&
            (action.name === AccordionAction.arrowBottom ||
              action.name === AccordionAction.arrowDown)),
        handler: action.handler(index),
        svgIcon: action.svgIcon,
        ariaLabel: action.ariaLabel,
      }));
  };

  private setReorderedRowsData = (newRowsData: IAccordionRowData<T>[]) => {
    const newKeysOrder = newRowsData.map((rowData) => rowData.uniqueKey);

    if (this.props.itemsReordered) {
      this.props.itemsReordered(newKeysOrder);
    }

    this.setState({ accordionRowsData: newRowsData });
  };

  private expandAll = () => {
    this.rowsRef.forEach((element) => element.expand());
  };

  private collapseAll = () => {
    this.rowsRef.forEach((element) => element.collapse());
  };

  private moveBottom = (index: number) => {
    return () =>
      this.moveAccordionRow(index, this.state.accordionRowsData.length - 1);
  };

  private moveTop = (index: number) => {
    return () => this.moveAccordionRow(index, 0);
  };

  private moveDown = (index: number) => {
    return () => this.moveAccordionRow(index, index + 1);
  };

  private moveUp = (index: number) => {
    return () => this.moveAccordionRow(index, index - 1);
  };

  private moveAccordionRow(oldIndex: number, newIndex: number) {
    const newRowsData = [...this.state.accordionRowsData];
    const checkedNewIndex =
      newIndex >= newRowsData.length || newIndex < 0
        ? newRowsData.length - 1
        : newIndex;

    const movedItem = newRowsData.splice(oldIndex, 1)[0];
    newRowsData.splice(checkedNewIndex, 0, movedItem);

    this.setReorderedRowsData(newRowsData);
  }

  private getActions = (uniqueKey: string | number): AccordionActionType[] => {
    return [
      {
        name: AccordionAction.arrowBottom,
        handler: this.moveBottom,
        svgIcon: <BottomIcon width={30} height={30} />,
        ariaLabel: "Move to bottom",
      },
      {
        name: AccordionAction.arrowTop,
        handler: this.moveTop,
        svgIcon: <TopIcon width={30} height={30} />,
        ariaLabel: "Move to top",
      },
      {
        name: AccordionAction.arrowDown,
        handler: this.moveDown,
        svgIcon: <ArrowIcon className="rotate-180" width={30} height={30} />,
        ariaLabel: "Move to bottom",
      },
      {
        name: AccordionAction.arrowUp,
        handler: this.moveUp,
        svgIcon: <ArrowIcon width={30} height={30} />,
        ariaLabel: "Move to top",
      },
      {
        name: AccordionAction.edit,
        handler: () => () =>
          this.props.actionHandler(uniqueKey, AccordionAction.edit),
        svgIcon: <EditIcon width={30} height={30} />,
        ariaLabel: "Edit",
      },
      {
        name: AccordionAction.delete,
        handler: () => () =>
          this.props.actionHandler(uniqueKey, AccordionAction.delete),
        svgIcon: <DeleteIcon width={30} height={30} />,
        ariaLabel: "Delete",
      },
    ];
  };
}
